import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Zap,
  CloudSun,
  Shield,
  Wrench,
  Flame,
  Lock,
  Sparkles,
  CheckCircle2,
  Factory,
  BadgeCheck,
  FileBadge,
  Award,
  ScrollText,
  Download,
} from "lucide-react";
import { company, stats, advantages, products, heroImage, workshopImage, qualityImage, marqueeItems, atest } from "../mock";
import { catalog, featured, coverOf } from "../catalog";
import Reveal from "../components/Reveal";
import LogisticsSection from "../components/LogisticsSection";
import { useAtest } from "../components/AtestProvider";

const iconMap = { CloudSun, Shield, Wrench, Flame, Zap, Lock };

export default function Home() {
  const { open: openAtest } = useAtest();
  const featuredProducts = featured.map((slug) => catalog.find((p) => p.slug === slug)).filter(Boolean);
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-neutral-950 text-white noise">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-amber-500/20 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-amber-400/10 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-16 md:pt-24 pb-24 md:pb-32 text-center flex flex-col items-center">
          <div className="w-full max-w-4xl">
            <Reveal>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs uppercase tracking-widest text-amber-300">
                <Sparkles className="w-3.5 h-3.5" />
                Njemački Kömmerling PVC • Proizvedeno u BiH
              </div>
            </Reveal>
            <Reveal delay={100}>
              <h1 className="font-display mt-6 text-5xl md:text-7xl lg:text-8xl font-bold leading-[0.95]">
                PVC elektro <br />
                ormari za <span className="text-amber-400">godine</span> <br />
                koje su pred Vama.
              </h1>
            </Reveal>
            <Reveal delay={200}>
              <p className="mt-6 text-lg text-neutral-300 max-w-2xl mx-auto leading-relaxed">
                {company.descriptionShort} {company.descriptionLong}
              </p>
            </Reveal>
            <Reveal delay={300}>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                <Link
                  to="/proizvodi"
                  className="btn-primary inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-neutral-950 font-semibold px-6 py-3.5 rounded-full animate-pulseGlow"
                >
                  Pogledaj ponudu
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/kontakt"
                  className="btn-primary inline-flex items-center gap-2 border border-white/20 hover:border-amber-400 hover:text-amber-400 px-6 py-3.5 rounded-full font-medium"
                >
                  Kontakt
                </Link>
              </div>
            </Reveal>

            <Reveal delay={400}>
              <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
                {stats.map((s) => (
                  <div key={s.label} className="text-center">
                    <div className="font-display text-4xl md:text-5xl font-bold text-white">{s.value}</div>
                    <div className="text-xs uppercase tracking-widest text-neutral-500 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          {/* removed hero image */}
          <div className="hidden">
            <Reveal delay={200}>
              <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl animate-float">
                <img src={heroImage} alt="Elektro ormar" className="w-full h-[480px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-tr from-neutral-950/70 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 flex items-center gap-3 bg-white/95 text-neutral-900 rounded-2xl p-4">
                  <div className="w-11 h-11 rounded-xl bg-amber-400 flex items-center justify-center">
                    <BadgeCheck className="w-6 h-6 text-neutral-950" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm">IP54 • Dvostruka izolacija</div>
                    <div className="text-xs text-neutral-600">Zaštita najvišeg standarda</div>
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -left-4 w-24 h-24 rounded-2xl bg-amber-400 rotate-6 -z-10 opacity-70 blur-sm" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-2xl bg-white -z-10 opacity-10" />
            </Reveal>
          </div>
        </div>

        {/* Marquee */}
        <div className="relative border-y border-white/10 bg-white/5 py-4 overflow-hidden">
          <div className="flex gap-12 animate-marquee whitespace-nowrap w-max">
            {[...marqueeItems, ...marqueeItems, ...marqueeItems].map((m, i) => (
              <span key={i} className="font-display text-2xl md:text-3xl text-white/60 flex items-center gap-8">
                {m}
                <Zap className="w-4 h-4 text-amber-400" />
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT / MISSION */}
      <section className="relative py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-5 space-y-6">
            <Reveal>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-neutral-100 text-xs uppercase tracking-widest text-neutral-700">
                <Factory className="w-3.5 h-3.5 text-amber-500" />
                Elorte-Medarić proizvodi
              </div>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight">
                Naša misija je pružiti proizvod koji će <span className="text-amber-500">nadmašiti očekivanja</span>.
              </h2>
            </Reveal>
            <Reveal delay={200}>
              <p className="text-neutral-600 leading-relaxed text-lg">
                Svi ormari se proizvode od vakumiranih PVC ploča CPT-OVO, debljine 3 i 4 mm.
                PVC ploče su samogasive i pružaju odličnu električnu zaštitu.
              </p>
            </Reveal>
            <Reveal delay={300}>
              <ul className="space-y-3 pt-2">
                {[
                  "Vakumirane PVC ploče debljine 3 i 4 mm",
                  "Samogasiva plastika klase CPT-OVO",
                  "Opremljivo prema uputstvima Elektrodistribucija",
                  "Prozori sa staklom 3 mm i PVC brtvom – IP54",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-neutral-800">
                    <CheckCircle2 className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <div className="lg:col-span-7 grid grid-cols-6 grid-rows-6 gap-4 h-[520px]">
            <Reveal className="col-span-4 row-span-4 relative overflow-hidden rounded-3xl bg-neutral-950 p-8 text-white">
              <div className="absolute inset-0 bg-grid opacity-30" />
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-amber-500/20 blur-3xl" />
              <div className="relative h-full flex flex-col justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-amber-400 flex items-center justify-center">
                    <Factory className="w-6 h-6 text-neutral-950" />
                  </div>
                  <div className="text-xs uppercase tracking-widest text-neutral-400">Radionica Jelah</div>
                </div>
                <div>
                  <div className="font-display text-4xl md:text-5xl font-bold leading-tight">
                    Preciznost <br /> & kvalitet.
                  </div>
                  <p className="mt-3 text-neutral-400 max-w-md">
                    Svaki ormar prolazi kroz strogu kontrolu kvaliteta — od izbora materijala do finalne montaže.
                  </p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={100} className="col-span-2 row-span-3 relative overflow-hidden rounded-3xl bg-white border border-neutral-200 p-6">
              <Zap className="w-8 h-8 text-amber-500" />
              <div className="mt-4 font-display text-4xl font-bold text-neutral-950">3–4 mm</div>
              <div className="text-sm text-neutral-600 mt-1">Debljina PVC ploča CPT-OVO</div>
            </Reveal>
            <Reveal delay={200} className="col-span-2 row-span-3 relative overflow-hidden rounded-3xl bg-neutral-950 p-6 text-white">
              <div className="absolute inset-0 bg-grid opacity-30" />
              <div className="relative h-full flex flex-col justify-between">
                <BadgeCheck className="w-8 h-8 text-amber-400" />
                <div>
                  <div className="font-display text-4xl font-bold">IP54</div>
                  <div className="text-sm text-neutral-400 mt-1">Stepen zaštite</div>
                </div>
              </div>
            </Reveal>
            <Reveal delay={300} className="col-span-4 row-span-2 relative overflow-hidden rounded-3xl bg-amber-400 p-6">
              <div className="flex items-center justify-between h-full">
                <div>
                  <div className="font-display text-2xl md:text-3xl font-bold text-neutral-950">Sigurnost prije svega</div>
                  <p className="text-neutral-900/80 mt-1 text-sm md:text-base">Dvostruka izolacija • IP54 • Staklo 3 mm sa gumenim okvirom</p>
                </div>
                <Shield className="w-16 h-16 text-neutral-950 hidden md:block" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ADVANTAGES */}
      <section className="py-24 md:py-32 bg-neutral-50 relative">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <Reveal>
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-neutral-200 text-xs uppercase tracking-widest text-neutral-700">
                  Prednosti PVC ormara
                </div>
                <h2 className="font-display mt-4 text-4xl md:text-5xl font-bold max-w-2xl leading-tight">
                  Zašto biraju <span className="text-amber-500">Elorte-Medarić</span>.
                </h2>
              </div>
            </Reveal>
            <Reveal delay={150}>
              <p className="text-neutral-600 max-w-md">
                Svaki ormar prolazi kroz strogu kontrolu kvaliteta – od izbora materijala do finalne montaže.
              </p>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {advantages.map((a, i) => {
              const Icon = iconMap[a.icon] || Zap;
              return (
                <Reveal key={a.title} delay={i * 60}>
                  <div className="group card-hover bg-white border border-neutral-200 rounded-2xl p-7 h-full hover:border-amber-400 hover:shadow-xl">
                    <div className="w-12 h-12 rounded-xl bg-neutral-950 group-hover:bg-amber-400 flex items-center justify-center transition-colors">
                      <Icon className="w-6 h-6 text-amber-400 group-hover:text-neutral-950 transition-colors" />
                    </div>
                    <h3 className="mt-5 font-display text-xl font-semibold">{a.title}</h3>
                    <p className="mt-2 text-neutral-600 leading-relaxed">{a.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ATEST SECTION */}
      <section className="relative py-24 md:py-32 bg-neutral-950 text-white overflow-hidden noise">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="absolute top-1/2 -translate-y-1/2 -right-40 w-[520px] h-[520px] rounded-full bg-amber-500/15 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs uppercase tracking-widest text-amber-300">
                <Award className="w-3.5 h-3.5" /> Atest • Certifikat kvaliteta
              </div>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="font-display mt-5 text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                Sigurnost sa <span className="text-amber-400">atestom</span>.
              </h2>
            </Reveal>
            <Reveal delay={200}>
              <p className="mt-6 text-lg text-neutral-300 leading-relaxed max-w-xl">
                {atest.description}
              </p>
            </Reveal>
            <Reveal delay={300}>
              <ul className="mt-8 space-y-3">
                {atest.points.map((p) => (
                  <li key={p} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                    <span className="text-neutral-200">{p}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={400}>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button
                  onClick={openAtest}
                  className="btn-primary inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-neutral-950 font-semibold px-6 py-3.5 rounded-full"
                >
                  Pogledaj atest <ScrollText className="w-4 h-4" />
                </button>
                <button
                  onClick={openAtest}
                  className="btn-primary inline-flex items-center gap-2 border border-white/20 hover:border-amber-400 hover:text-amber-400 text-white px-6 py-3.5 rounded-full font-medium"
                >
                  Preuzmi PDF / sliku <Download className="w-4 h-4" />
                </button>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-6 relative">
            {/* Certificate card */}
            <Reveal delay={200}>
              <div className="relative bg-gradient-to-br from-white to-neutral-100 text-neutral-900 rounded-3xl p-8 shadow-2xl border border-white/20 animate-float">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-2xl bg-neutral-950 flex items-center justify-center">
                      <FileBadge className="w-7 h-7 text-amber-400" />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Zvanični dokument</div>
                      <div className="font-display font-bold text-xl leading-tight">ATEST</div>
                      <div className="text-xs text-neutral-500">Elorte-Medarić d.o.o.</div>
                    </div>
                  </div>
                  <div className="w-16 h-16 rounded-full border-4 border-amber-400 flex items-center justify-center rotate-[-8deg]">
                    <div className="text-center leading-none">
                      <div className="text-[10px] uppercase tracking-widest text-amber-600 font-semibold">Ovjereno</div>
                      <div className="text-xs font-bold text-neutral-900 mt-0.5">2026</div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-3">
                  {atest.items.map((it) => (
                    <div key={it.code} className="bg-white rounded-2xl p-4 border border-neutral-200 hover:border-amber-400 transition-colors card-hover">
                      <div className="inline-flex items-center gap-2 px-2 py-0.5 rounded-full bg-amber-400 text-neutral-950 text-[10px] font-bold uppercase tracking-widest">
                        {it.code}
                      </div>
                      <div className="mt-2 font-semibold text-sm">{it.title}</div>
                      <div className="mt-1 text-xs text-neutral-600 leading-snug">{it.desc}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-5 border-t border-neutral-200 flex items-center justify-between">
                  <div className="text-xs text-neutral-500">
                    <div className="uppercase tracking-widest text-[10px]">Uskla\u0111eno sa</div>
                    <div className="font-semibold text-neutral-900 mt-0.5">BAS / EN / IEC standardi</div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-neutral-500">
                    <BadgeCheck className="w-4 h-4 text-amber-500" />
                    Va\u017ee\u0107i
                  </div>
                </div>
              </div>

              <div className="absolute -top-4 -right-4 w-24 h-24 rounded-2xl bg-amber-400 rotate-6 -z-10 opacity-70 blur-sm" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* LOGISTICS */}
      <LogisticsSection />


      {/* PRODUCTS PREVIEW */}
      <section id="Ponuda" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <Reveal>
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-neutral-100 text-xs uppercase tracking-widest text-neutral-700">
                  Iz vlastite ponude izdvajamo
                </div>
                <h2 className="font-display mt-4 text-4xl md:text-5xl font-bold max-w-2xl leading-tight">
                  Naši proizvodi.
                </h2>
              </div>
            </Reveal>
            <Reveal delay={150}>
              <Link
                to="/proizvodi"
                className="btn-primary inline-flex items-center gap-2 bg-neutral-950 hover:bg-amber-400 hover:text-neutral-950 text-white px-5 py-3 rounded-full text-sm font-medium"
              >
                Sva ponuda <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {featuredProducts.map((p, i) => (
              <Reveal key={p.slug} delay={i * 80}>
                <Link to="/proizvodi" className="group card-hover bg-neutral-50 rounded-3xl overflow-hidden border border-neutral-200 hover:border-amber-400 h-full block">
                  <div className="aspect-[4/5] overflow-hidden bg-neutral-950 relative">
                    <img src={coverOf(p)} alt={p.name} className="w-full h-full object-contain p-6 group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-3 left-3 bg-amber-400 text-neutral-950 text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-widest">
                      {p.short}
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-lg font-semibold group-hover:text-amber-600 transition-colors">{p.name}</h3>
                    <p className="mt-2 text-sm text-neutral-600 line-clamp-3">{p.description}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-neutral-950 relative overflow-hidden noise">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="absolute inset-y-0 left-0 w-1/2 pointer-events-none">
          <div className="absolute top-1/2 -translate-y-1/2 w-full h-1 bg-gradient-to-r from-transparent via-amber-400 to-transparent animate-spark" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white leading-tight">
              Imate pitanje za nas?
            </h2>
            <p className="mt-4 text-neutral-400 max-w-md">
              Za sva pitanja, primjedbe i sugestije pišite nam na info@elorte-medaric.ba – odgovaramo u što kraćem roku.
            </p>
          </div>
          <div className="flex md:justify-end">
            <Link
              to="/kontakt"
              className="btn-primary inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-neutral-950 font-semibold px-8 py-4 rounded-full text-lg"
            >
              KONTAKT <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
